/**
 * navigation.service.ts
 *
 * Fetches account branding and navigation items from the server.
 * The response is wrapped under a `main` key by the v4-compat layer.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';
import { facilityScopeParams } from '@/lib/facilityScopeParams';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface NavItem {
  key: string;
  label: string;
  url: string;
  icon?: string;
  badge?: number;
}

export interface AccountBranding {
  logo: string | null;
  logoSquare: string | null;
  bgColor: string | null;
  buttonColor: string | null;
  name: string;
  /** Optional caption the account types in for under the site logo. Blank = none. */
  logoSubText?: string;
  /** Caption placement within the logo's own width. */
  logoSubTextAlign?: 'left' | 'center' | 'right';
  /** Font key, mapped to a stack by logoSubTextFonts. Blank = inherit. */
  logoSubTextFont?: string;
  /** Caption font size in px. 0 = the client default. */
  logoSubTextSize?: number;
  /** Letter spacing in px; may be negative. 0 and unset both mean normal. */
  logoSubTextLetterSpacing?: number;
  /** Caption colour, 6 hex digits without the #. Blank = sidebar default. */
  logoSubTextColor?: string;
  /** Vertical nudge in px, negative being up over the logo. 0 = in flow. */
  logoSubTextOffsetY?: number;
}

export interface NavigationUser {
  userID: string;
  name: string;
  initials: string;
  firstName: string;
  lastName: string;
  email: string;
  userLevel: string;
  /** Raw `users.role` (e.g. "Doctor"); null when no role assigned. */
  role: string | null;
  /**
   * Account-customized display title for the user's role, resolved
   * from `aa_accounts_roles` server-side. Null when the user has no
   * role configured.
   */
  roleTitle: string | null;
  avatarUrl: string | null;
}

export interface NavigationData {
  user: NavigationUser;
  branding: AccountBranding;
  items: NavItem[];
}

export interface NavigationBadges {
  messages: number;
  tasks: number;
  qa: number;
  leads: number;
  /** Lab results waiting to be matched to a patient record. */
  labs: number;
  /**
   * Patient conversations where a colleague @mentioned the signed-in user and
   * they have not opened it yet. Optional: absent from an API that predates it.
   */
  mentions?: number;
  /** Google reviews with no reply yet. Cached server-side; may be ~15 min stale. */
  googleReviews: number;
}

// ─── API calls ──────────────────────────────────────────────────────────────

export async function getNavigation(): Promise<NavigationData> {
  return apiClient.get<ApiResponse<NavigationData>>('/navigation/main').then((r) => r.data.data);
}

/**
 * Nav counts scoped to the selected facilities. Takes the full selection, or a
 * single id as before. One facility sends exactly `{ facilityId }` and none
 * sends no params, both as they always have; several send `facilityIds[]`.
 */
export async function getNavigationBadges(
  facilities?: readonly number[] | number | null,
): Promise<NavigationBadges> {
  const ids = Array.isArray(facilities)
    ? facilities
    : typeof facilities === 'number' ? [facilities] : [];
  const scope = facilityScopeParams(ids);
  const params = Object.keys(scope).length > 0 ? scope : undefined;
  return apiClient
    .get<ApiResponse<NavigationBadges>>('/navigation/badges', { params })
    .then((r) => r.data.data);
}
